import {
  Component, OnDestroy, OnInit, Signal, computed, effect, inject, untracked,
} from '@angular/core';
import {
  AccountService,
  AnalyticsService, EventSearchService, GeoLocationService, LayoutMode, LayoutService,
  QueryHandler, SEOService, SearchService,
} from '../../services';
import { LoadingStateComponent } from '../../core/components';
import { skip } from 'rxjs';
import { HomePageInfoSnapshot, HomePageService } from '../../services/home-page.service';
import {
  Country, EventCategory, EventInfo, isNullOrUndefined, isNullOrUndefinedOrEmpty
} from 'in-time-core';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MetaMixin } from '../../core/mixins/meta-mixin';
import { getEventCategoryForRouteParam } from './utility/route-utils';
import { concatComparators } from '../../core/utils/comparison-utils';
import {
  compareEventsByCountry, compareEventsByFeaturedFlag,
  compareEventsByFeaturedScore, compareEventsByStartDate
} from '../../core/utils/event-info-utils';

type EventCategoryDistribution = Record<EventCategory, EventInfo[]>;

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
})
export class HomePageComponent extends MetaMixin(LoadingStateComponent) implements OnInit, OnDestroy {
  protected readonly seoService: SEOService = inject(SEOService);
  private readonly accountService = inject(AccountService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly homePageService: HomePageService = inject(HomePageService);
  private readonly eventSearchService: EventSearchService = inject(EventSearchService);
  private readonly geoLocationService: GeoLocationService = inject(GeoLocationService);

  private readonly layoutService = inject(LayoutService);
  private readonly searchService = inject(SearchService);

  private readonly queryHandler = new QueryHandler(this.constructor.name);

  private readonly homePageInfoSnapshot = toSignal(this.homePageService.homePageInfoSnapshots$.pipe(skip(1)), {
    initialValue: this.activatedRoute.snapshot.data['homePageInfoSnapshot'] as HomePageInfoSnapshot,
  });

  private readonly eventsTableMap = new Map<string, EventInfo>();
  private readonly eventsTable = computed(() => {
    const snapshot = this.homePageInfoSnapshot();

    if(isNullOrUndefined(snapshot)) {
      return this.eventsTableMap;
    }

    if(!snapshot.success) {
      return this.eventsTableMap;
    }

    const events = snapshot.data.events;

    this.eventsTableMap.clear();
    for(const event of events) {
      this.eventsTableMap.set(event.uniqueId, event);
    }

    return this.eventsTableMap;
  });

  private readonly queryParams = toSignal(
    this.activatedRoute.queryParams, {
      initialValue: this.activatedRoute.snapshot.queryParams
    },
  );

  private readonly queryInUrl: Signal<string> = computed(() => {
    const queryParams = this.queryParams();
    const query = queryParams['query'];
    if(typeof query === 'string') {
      return query;
    }

    return '';
  });

  private readonly query = toSignal(this.queryHandler.query$.pipe(skip(1)), {
    initialValue: this.queryInUrl(),
  });

  constructor() {
    super();

    this.searchService.pushHandler(this.queryHandler);

    // Ensure that the search field matches the initial URL query.
    const initialQuery = this.queryInUrl();
    const fieldValue = this.queryHandler.query;
    if(fieldValue !== initialQuery) {
      this.searchService.search(initialQuery, true);
    }

    // Sync the search field with the URL query.
    effect(() => {
      const urlQuery = this.queryInUrl();

      untracked(() => {
        const query = this.query();

        if(urlQuery !== query) {
          this.searchService.search(urlQuery, true);
        }
      });
    });

    // Sync the URL query with the search field.
    effect(() => {
      const query = this.query();

      untracked(() => {
        const urlQuery = this.queryInUrl();

        if(urlQuery !== query) {
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { query: query === '' ? undefined : query },
            queryParamsHandling: 'merge',
          });
        }
      });
    });

    // Track search events.
    effect(() => {
      const query = this.query();

      this.analyticsService.trackSearch({
        query: query,
        category: 'event',
        queryParams: this.activatedRoute.snapshot.queryParams,
        email: this.accountService?.account?.email ?? null,
        phoneNumber: this.accountService?.account?.phoneNumber ?? null,
      });
    });
  }

  private readonly paramMap = toSignal(this.activatedRoute.paramMap, {
    initialValue: this.activatedRoute.snapshot.paramMap,
  });

  public readonly selectedCategory = computed(() => {
    const paramMap = this.paramMap();

    const category = paramMap.get('category');
    if(isNullOrUndefined(category)) {
      return null;
    }

    return getEventCategoryForRouteParam(category) ?? null;
  });

  public readonly searchResults = computed(() => {
    const query = this.query();
    const category = this.selectedCategory();
    if(isNullOrUndefinedOrEmpty(query) && isNullOrUndefined(category)) {
      return null;
    }

    const result = this.findEvents(query, category);

    const userCountry = this.geoLocationService.country ?? Country.Hungary;
    result.sort(concatComparators(
      compareEventsByCountry(userCountry),
      compareEventsByStartDate,
      compareEventsByFeaturedFlag,
      compareEventsByFeaturedScore,
    ));

    return result;
  });

  private findEvents(query: string | null | undefined, category: EventCategory | null | undefined): EventInfo[] {
    const eventsTable = this.eventsTable();

    function matchesCategory(event: EventInfo): boolean {
      if(isNullOrUndefined(category)) {
        return true;
      }

      if(category === EventCategory.Other) {
        const hasNoClassification = event.classificationInfo.classification.length <= 0;
        const isOther = event.classificationInfo.classification.indexOf(EventCategory.Other) >= 0;

        return hasNoClassification || isOther;
      }

      return event.classificationInfo.classification.indexOf(category) >= 0;
    }

    if(isNullOrUndefinedOrEmpty(query)) {
      return [...eventsTable.values()].filter(matchesCategory);
    }
    else {
      const searchResult = this.eventSearchService.search(query) ?? [];
      const foundEvents: EventInfo[] = [];
      for(const uniqueId of searchResult) {
        const event = eventsTable.get(uniqueId);
        if(isNullOrUndefined(event)) {
          continue;
        }

        if(!matchesCategory(event)) {
          continue;
        }

        foundEvents.push(event);
      }

      return foundEvents;
    }
  }

  public readonly featuredEvents = computed(() => {
    const eventsTable = this.eventsTable();

    const result = [];
    for(const event of eventsTable.values()) {
      if(event.classificationInfo.isFeatured) {
        result.push(event);
      }
    }

    const userCountry = this.geoLocationService.country ?? Country.Hungary;
    result.sort(concatComparators(
      compareEventsByCountry(userCountry),
      compareEventsByFeaturedScore,
      compareEventsByStartDate,
    ));

    return result;
  });

  public readonly defaultCategoryOrder = [
    EventCategory.Party,
    EventCategory.Theater,
    EventCategory.Comedy,
    EventCategory.PerformanceEvening,
    EventCategory.Concert,
    EventCategory.Festival,
    EventCategory.Sport,
    EventCategory.Performance,
    EventCategory.Gastro,
    // NOTE(emil): hide other category for now, only "secret" events will be in this category.
    // EventCategory.Other,
  ];

  public readonly categoryDistribution = computed<EventCategoryDistribution>(() => {
    const eventsTable = this.eventsTable();

    const result: EventCategoryDistribution = {
      [EventCategory.Party]: [],
      [EventCategory.Theater]: [],
      [EventCategory.Comedy]: [],
      [EventCategory.Concert]: [],
      [EventCategory.Festival]: [],
      [EventCategory.Other]: [],
      [EventCategory.PerformanceEvening]: [],
      [EventCategory.Sport]: [],
      [EventCategory.Performance]: [],
      [EventCategory.Gastro]: [],
    };

    for(const event of eventsTable.values()) {
      const classification = event.classificationInfo.classification;
      if(classification.length <= 0) {
        result[EventCategory.Other].push(event);
        continue;
      }

      for(const category of classification) {
        result[category].push(event);
      }
    }

    const userCountry = this.geoLocationService.country ?? Country.Hungary;
    for(const category of Object.values(EventCategory)) {
      result[category].sort(concatComparators(
        compareEventsByCountry(userCountry),
        compareEventsByFeaturedScore,
        compareEventsByStartDate,
      ));
    }

    return result;
  });

  public readonly categoryOrder = computed(() => {
    const categoryDistribution = this.categoryDistribution();

    const result = [];
    for(const category of this.defaultCategoryOrder) {
      if(categoryDistribution[category].length > 1) {
        result.push(category);
      }
    }

    return result;
  });

  ngOnInit(): void {
    this.seoService.setCanonicalURLTag();
    this.setGenericMetaTags();
    this.initAnalytics();

    this.layoutService.push({
      headerMode: LayoutMode.MobileAndDesktop,
    });
  }

  ngOnDestroy(): void {
    this.layoutService.pop();
    this.searchService.popHandler((h) => h.uniqueId == this.queryHandler.uniqueId);
    this.queryHandler.reset();
  }

  private initAnalytics(): void {
    this.analyticsService.trackPageView({
      title: 'Home Page',
      queryParams: this.activatedRoute.snapshot.queryParams,
    });
  }
}
